class ArticleToc extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.article-toc__list');
    const selector = this.getAttribute('data-content-selector') || '.article-template__content';
    this.content = document.querySelector(selector);

    if (!this.list || !this.content) return;

    this.headings = this.collectHeadings();
    if (this.headings.length < 2) return;

    this.buildList();
    this.removeAttribute('hidden');
    this.bindEvents();
    this.updateActive();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  collectHeadings() {
    const used = new Set();

    return Array.from(this.content.querySelectorAll('h2'))
      .map((node) => {
        const text = node.textContent.trim();
        if (!text) return null;

        let id = node.id;
        if (!id) {
          const base = this.slugify(text);
          id = base;
          let suffix = 2;
          while (used.has(id) || document.getElementById(id)) {
            id = `${base}-${suffix}`;
            suffix += 1;
          }
          node.id = id;
        }

        used.add(id);
        node.classList.add('article-content-heading');
        return { id, text, node };
      })
      .filter(Boolean);
  }

  slugify(text) {
    const slug = text
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\p{N}\s-]/gu, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || 'section';
  }

  buildList() {
    const fragment = document.createDocumentFragment();

    this.links = this.headings.map(({ id, text }) => {
      const item = document.createElement('li');
      item.className = 'article-toc__item';

      const link = document.createElement('a');
      link.className = 'article-toc__link';
      link.href = `#${id}`;

      const bullet = document.createElement('span');
      bullet.className = 'article-toc__bullet';
      bullet.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'article-toc__text';
      label.textContent = text;

      link.append(bullet, label);
      item.append(link);
      fragment.append(item);
      return link;
    });

    this.list.replaceChildren(fragment);
  }

  bindEvents() {
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.updateActive();
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.list.addEventListener('click', (event) => this.onLinkClick(event));
  }

  onLinkClick(event) {
    const link = event.target.closest('.article-toc__link');
    if (!link) return;

    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = target.getBoundingClientRect().top + window.scrollY - this.scrollOffset;

    window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    history.replaceState(null, '', `#${id}`);
  }

  get scrollOffset() {
    const header = document.querySelector('.section-header, .header-wrapper');
    const headerHeight = header ? header.offsetHeight : 0;
    return headerHeight + 24;
  }

  updateActive() {
    const trigger = this.scrollOffset + 16;
    let activeId = this.headings[0].id;

    for (const heading of this.headings) {
      if (heading.node.getBoundingClientRect().top - trigger <= 0) {
        activeId = heading.id;
      } else {
        break;
      }
    }

    const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;
    if (atBottom) {
      activeId = this.headings[this.headings.length - 1].id;
    }

    if (activeId === this.activeId) return;
    this.activeId = activeId;

    this.links.forEach((link) => {
      const isActive = link.getAttribute('href') === `#${activeId}`;
      link.classList.toggle('is-active', isActive);
      link.parentElement.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }
}

customElements.define('article-toc', ArticleToc);
